import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuthState, useContributorsActions, useContributorsState } from "@/store";
import useClickOutside from "@/components/hooks/useClickOutside";
import {
  FormContainer,
  FormGroup,
  DropdownWrapper,
  SelectTrigger,
  SelectedValue,
  ChevronIcon,
  FilterIcon,
  SelectedIcon,
  DropdownMenu,
  Input,
  DropdownItem,
  DropdownDivider,
  MobileSelectTrigger,
} from "./ContributorSelector.elements";
import { useIsMobile } from "../hooks";

const defaultValue = "All Contributors";

const ContributorsSelector = () => {
  const {
    currentUser,
    fetch: { status: fetchAuthStatus },
  } = useAuthState();
  const normalizedRole = (currentUser?.role || "").toLowerCase();
  const {
    contributors,
    selectedContributorId,
    fetch: { status: fetchContributorsStatus },
  } = useContributorsState();
  const { fetchContributors, selectContributorId } = useContributorsActions();

  const [filteredContributors, setFilteredContributors] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedContributorEmail, setSelectedContributorEmail] = useState(defaultValue);

  const isMobile = useIsMobile();
  const dropdownRef = useRef(null);

  const canViewContributors =
      fetchAuthStatus === "succeeded" && (normalizedRole === "admin" || normalizedRole === "manager");

  useEffect(() => {
    if (fetchContributorsStatus === "idle" && canViewContributors) fetchContributors();
  }, [canViewContributors, fetchContributors, fetchContributorsStatus]);

  useEffect(() => {
    const normalizedSearch = (searchTerm || "").toLowerCase();
    const filtered = (contributors || []).filter((contributor) => {
      const haystack = `${contributor?.name || ""} ${contributor?.email || ""}`.toLowerCase();
      return haystack.includes(normalizedSearch);
    });
    setFilteredContributors(filtered);
  }, [searchTerm, contributors]);

  useEffect(() => {
    if (!selectedContributorId) {
      setSelectedContributorEmail(defaultValue);
      return;
    }
    const match =
        (contributors || []).find((contributor) => contributor.id === selectedContributorId) ||
        (currentUser?.id === selectedContributorId ? currentUser : null);
    if (match) {
      setSelectedContributorEmail(match.email || match.name || defaultValue);
    }
  }, [selectedContributorId, contributors, currentUser]);

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const handleSelectContributor = useCallback(
      (contributor) => {
        const selectedEmail = contributor?.email || contributor;
        setSelectedContributorEmail(selectedEmail);
        setShowDropdown(false);
        selectContributorId(contributor?.id || "");
      },
      [selectContributorId]
  );

  useClickOutside(dropdownRef, () => setShowDropdown(false));

  if (!canViewContributors) return null;

  return (
      <FormContainer>
        <FormGroup>
          <DropdownWrapper ref={dropdownRef}>
            {isMobile ? (
                <MobileSelectTrigger onClick={() => setShowDropdown((v) => !v)}>
                  <FilterIcon />
                </MobileSelectTrigger>
            ) : (
                <SelectTrigger onClick={() => setShowDropdown((v) => !v)}>
                  <FilterIcon />
                  <SelectedValue>{selectedContributorEmail}</SelectedValue>
                  <ChevronIcon open={showDropdown} />
                </SelectTrigger>
            )}

            {showDropdown && (
                <DropdownMenu>
                  <Input
                      type="text"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={handleSearchChange}
                  />

                  <DropdownItem onClick={() => handleSelectContributor({ email: defaultValue, id: "" })}>
                    <div style={{ width: "100%" }}>
                      <strong>All Contributors</strong>
                    </div>
                    {selectedContributorEmail === defaultValue && <SelectedIcon />}
                  </DropdownItem>

                  <DropdownItem onClick={() => handleSelectContributor(currentUser)}>
                    <div style={{ width: "100%" }}>
                      <strong>Just Me</strong>
                    </div>
                    {selectedContributorEmail === currentUser?.email && <SelectedIcon />}
                  </DropdownItem>

                  <DropdownDivider />

                  {filteredContributors.map((contributor) => (
                      <DropdownItem
                          key={contributor.id}
                          onClick={() => handleSelectContributor(contributor)}
                      >
                        <div style={{ width: "100%" }}>
                          <strong>{contributor.name}</strong>
                          <div
                              style={{
                                fontSize: "12px",
                                color: "#aaa",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                          >
                            {contributor.email}
                          </div>
                        </div>
                        {selectedContributorEmail === contributor.email && <SelectedIcon />}
                      </DropdownItem>
                  ))}
                </DropdownMenu>
            )}
          </DropdownWrapper>
        </FormGroup>
      </FormContainer>
  );
};

export default ContributorsSelector;
